import React, { useState, useEffect } from 'react';
import { Section } from 'react-scale/containers/corp/backfills/batch/components/Section';
import TextInput from 'design-system/TextInput';
import { Button } from 'design-system/Button';
import { Select, Option } from 'react-scale/components/design-system/Select';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import SectionTitle from 'corp/components/UI/SectionTitle';
import { requiredAsterisk } from 'react-scale/corp/helpers';
import { makeStyles } from 'design-system/styles';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';

const placeholder = `
Select a template
`;

const loadingTemplateText = `
Loading...
`;

const useStyles = makeStyles({
  horizontalCenter: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  generateTopicCsv: {
    display: 'flex',
    flexDirection: 'column',
    '&__templateButtonBar': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: '1rem',
    },
    '&__subsectionTitle': {
      display: 'flex',
      flexDirection: 'row',
      '&Element': {
        '&:not(:last-child)': {
          marginRight: '.5rem',
        },
      },
    },
    '&__subsection': {
      '&:not(:last-child)': {
        marginBottom: '2rem',
      },
    },
    '&__markdownPreview': {
      borderRadius: '5px',
      backgroundColor: 'lightgray',
      padding: '1rem',
    },
  },
});

const templateLabels: string[] = ['basic_topic'];

function GenerateTopicCsv() {
  const [howManyTopics, setHowManyTopics] = useState('');
  const [excludedTopics, setExcludedTopics] = useState('');
  const [template, setTemplate] = useState<string>('');
  const [templateText, setTemplateText] = useState(placeholder);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [creatingTopics, setCreatingTopics] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    async function fetchTemplate() {
      const url = `corp-api/flamingo-utils/llm-templates/${template}`;

      try {
        const { templateText } = await apiFetch(null, url, { method: 'GET' });
        setTemplateText(templateText);
      } catch (error) {
        defaultApiFetchErrorHandler(error);
      }

      setLoadingTemplate(false);
    }

    fetchTemplate();
  }, [template]);

  function onChangeHowManyTopics(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setHowManyTopics(value);
  }

  function onChangeExcludedTopics(event: React.ChangeEvent<HTMLInputElement>) {
    setExcludedTopics(event.target.value.trim());
  }

  async function handleSelectTemplate(event: React.ChangeEvent<{ value: string }>) {
    const {
      target: { value: template },
    } = event;

    setTemplate(template);
    setLoadingTemplate(true);
  }

  async function handleUpdateTemplate() {
    const url = `corp-api/flamingo-utils/llm-templates/${template}`;
    setLoadingTemplate(true);

    try {
      await apiFetch(null, url, {
        method: 'POST',
        body: {
          templateText,
        },
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error);
    }

    setLoadingTemplate(false);
  }

  async function handleCreateTopics() {
    const url = `corp-api/flamingo-utils/topics`;
    setCreatingTopics(true);

    try {
      const { filename, data } = await apiFetch(null, url, {
        method: 'GET',
        query: {
          templateName: template,
          howMany: howManyTopics,
          userExcludedWords: excludedTopics,
        },
      });

      const blob = new Blob(data);
      const a = window.document.createElement('a');

      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      defaultApiFetchErrorHandler(error);
    }

    setCreatingTopics(false);
  }

  function handleEditTemplateText(event: React.ChangeEvent<{ value: string }>) {
    setTemplateText(event.target.value);
  }

  const disableTemplateEdition = template === '' || loadingTemplate;
  const disableSubmit = disableTemplateEdition || howManyTopics === '' || creatingTopics;

  return (
    <div className={classes.generateTopicCsv}>
      <section className={`container ${classes.generateTopicCsv}__subsection`}>
        <div className={`${classes.generateTopicCsv}__subsectionTitle`}>
          <div className={`medium-text ${classes.generateTopicCsv}__subsectionTitleElement`}>
            Generate for how many topics?
          </div>{' '}
          {requiredAsterisk}
        </div>
        <div>
          <TextInput value={howManyTopics} onChange={onChangeHowManyTopics} />
        </div>
      </section>
      <section className={`container ${classes.generateTopicCsv}__subsection`}>
        <div className={`${classes.generateTopicCsv}__subsectionTitle`}>
          <div className={`medium-text ${classes.generateTopicCsv}__subsectionTitleElement`}>
            Should any topics be excluded? Provide list here:
          </div>
        </div>
        <div>
          <TextInput
            value={excludedTopics}
            onChange={onChangeExcludedTopics}
            rowsMax={10}
            multiline
            fullWidth
          />
        </div>
      </section>
      <section className={`container ${classes.generateTopicCsv}__subsection`}>
        <div className={`${classes.generateTopicCsv}__subsectionTitle`}>
          <div className={`medium-text ${classes.generateTopicCsv}__subsectionTitleElement`}>
            Which template? Remember to use {'{{'} topic {'}}'} as the token to be replaced by the
            specific topic
          </div>
        </div>
        <div className={`${classes.generateTopicCsv}__templateButtonBar`}>
          <Select placeholder="Select a Template" value={template} onChange={handleSelectTemplate}>
            {templateLabels.map(name => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          {/* <Button onClick={handleCancelTemplateUpdate} type={'error'}>
            Cancel
          </Button> */}
          <Button onClick={handleUpdateTemplate} type={'primary'} disabled={disableTemplateEdition}>
            {loadingTemplate ? 'LOADING' : 'Update template on S3'}
          </Button>
        </div>
        <div>
          <TextInput
            value={loadingTemplate ? loadingTemplateText : templateText}
            onChange={handleEditTemplateText}
            rows={15}
            multiline={true}
            fullWidth
            disabled={disableTemplateEdition}
          />
        </div>
      </section>
      <section
        className={`${classes.generateTopicCsv}__subsection ${classes.generateTopicCsv}__markdownPreview`}
      >
        <SanitizedMarkdown text={templateText} />
      </section>
      <section className={`${classes.horizontalCenter}`}>
        <Button onClick={handleCreateTopics} type={'primary'} disabled={disableSubmit}>
          {creatingTopics ? 'Creating Topics...' : 'Create Topics'}
        </Button>
      </section>
    </div>
  );
}

function FlamingoFairUtilsPage() {
  return (
    <div className="root">
      <SectionTitle title="Flamingo Fair Actions" />
      <Section name={'Generate Topic CSV'} forceHidden={true}>
        <GenerateTopicCsv />
      </Section>
    </div>
  );
}

export default FlamingoFairUtilsPage;
